import ModalsManagerContext, { IModalOptions } from "contexts/ModalsManager";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import ArrowsIcon from "assets/icons/arrows.svg?react";
import CloseIcon from "assets/icons/close.svg?react";
import DiskIcon from "assets/icons/credit_card.svg?react";
import SecurityIcon from "assets/icons/fingerprint.svg?react";
import DriveIcon from "assets/icons/hard_drive.svg?react";
import IntegrationIcon from "assets/icons/integration.svg?react";
import OutgoingIcon from "assets/icons/new-outcoming.svg?react";
import FlagIcon from "assets/icons/flag_border.svg?react";
import PersonalIcon from "assets/icons/personal.svg?react";
import EDeliveryIcon from "assets/icons/profile.svg?react";
import RingIcon from "assets/icons/ring.svg?react";
import TagsIcon from "assets/icons/tags.svg?react";
import TeamIcon from "assets/icons/team.svg?react";
import ViewIcon from "assets/icons/palette.svg?react";
import PersonIcon from "assets/icons/person.svg?react";
import EmailIcon from "assets/icons/email.svg?react";
import PaymentsTab from "components/Settings/PaymentsTab";
import { useAuth } from "react-oidc-context";
import BasicTab from "../BasicTab";
import ClassificationStatusTab from "../ClassificationStatusTab";
import ClassificationTab from "../ClassificationTab";
import DiskTab from "../DiskTab";
import EDeliveryTab from "../EDeliveryTab";
import IntegrationsTab from "../IntegrationsTab";
import Integrations1Tab from "../Integrations1Tab";
import PersonalTab from "../PersonalTab";
import SecurityTab from "../SecurityTab";
import TabButton from "../TabButton";
import WorkersTab from "../WorkersTab";
import ViewTab from "../ViewTab";
import RolesTab from "../RolesTab";
import EmailTab from "../EmailTab";
import DictionariesTab from "../DictionariesTab";
import MailroomTab from "../MailroomTab";
import BudzetFkTab from "../BudzetFkTab";
import BookIcon from "assets/icons/book.svg?react";
import HomeWorkIcon from "assets/icons/home_work.svg?react";
import ShareIcon from "assets/icons/share.svg?react";
import EncryptedIcon from "assets/icons/encrypted.svg?react";
import WorkflowModule from "modules/workflowModule";
import ESzyfrowaneTab from "../ESzyfrowaneTab";
import { AbilityContext } from "contexts/AbilityContext";
import { useTranslation } from "react-i18next";

export enum SettingsTabs {
  PersonalData,
  Workers,

  Permission,
  Basic,
  Integrations,
  Classification,
  ClassificationStatus,
  Roles,
  Email,
  PACKAGE,
  EDelivery,
  HardDrive,
  Security,
  Payments,
  ViewSettings,
  Dictionaries,
  Workflow,
  Integrations1,
  ESzyfrowane,
  BudzetFk,
  Mailroom,
}

export interface ISettingsModalOptions extends IModalOptions {
  initialTab: SettingsTabs;
}

const SettingsModal: FC<Props> = ({ label, onClose }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const isGuest = auth?.user?.profile?.role === "guest";

  const { modals, setModals, options, setOptions } =
    useContext(ModalsManagerContext);
  const [tab, setTab] = useState<SettingsTabs>(
    options && "initialTab" in options
      ? (options.initialTab as SettingsTabs)
      : isGuest
      ? SettingsTabs.EDelivery
      : SettingsTabs.PersonalData,
  );

  useEffect(() => {
    if (options) {
      setOptions(undefined);
    }
  }, []);

  const ability = useContext(AbilityContext);

  const [, setShowTabBar] = useState<boolean>(true);

  const guestTabs = [
    {
      type: SettingsTabs.Workers,
      header: t("settings.teamHeader"),
      icon: TeamIcon,
      visible: true,
    },
    {
      type: SettingsTabs.EDelivery,
      header: t("settings.edeliveryTitle"),
      icon: EDeliveryIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Integrations,
      header: t("integrations.title"),
      icon: IntegrationIcon,
      visible: true,
    },
  ];

  // Zakładki użytkownika niebędącego gościem — JEDEN zestaw (D1: jedna firma na
  // użytkownika, bez przełącznika kontekstu). Podział na „Ustawienia konta" i „Ustawienia
  // profilów" miał sens, gdy profileTabs zależały od profilu wybranego w comboboxie —
  // combobox zniknął razem z przełącznikiem, więc podział stracił uzasadnienie.
  // Permission/„Uprawnienia" (subkonta) wypadła w całości — Q8 (docs/model-kluczy-firmowych.md):
  // subkonta znikają na rzecz ról. AddPermission/PermissionUser skasowane w F7 razem
  // z BE (SubaccountsRepository/SubaccountController) — nie miały żadnego konsumenta.
  const tabs = [
    {
      type: SettingsTabs.Basic,
      header: t("notifications.title"),
      icon: RingIcon,
      visible: true,
    },
    {
      type: SettingsTabs.PersonalData,
      header: t("settings.tabs.personalData"),
      icon: PersonalIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Security,
      header: t("settings.tabs.security"),
      icon: SecurityIcon,
      visible: true,
    },
    {
      type: SettingsTabs.ViewSettings,
      header: t("settings.view.title"),
      icon: ViewIcon,
      visible: true,
    },
    {
      type: SettingsTabs.HardDrive,
      header: t("settings.tabs.dataInNota"),
      icon: DriveIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Workers,
      header: t("settings.teamHeader"),
      icon: TeamIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Basic,
      header: t("notifications.title"),
      icon: RingIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Integrations,
      header: t("integrations.title"),
      icon: ArrowsIcon,
      visible: false,
    },
    {
      type: SettingsTabs.Integrations1,
      header: "Integracje 1",
      icon: ArrowsIcon,
      visible: true,
    },
    {
      type: SettingsTabs.ESzyfrowane,
      header: "eSzyfrowane",
      icon: EncryptedIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Classification,
      header: t("settings.tabs.labels"),
      icon: TagsIcon,
      visible: true,
    },
    {
      type: SettingsTabs.ClassificationStatus,
      header: t("settings.tabs.statuses"),
      icon: FlagIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Roles,
      header: t("roles.title"),
      icon: PersonIcon,
      visible: false,
    },
    {
      type: SettingsTabs.Email,
      header: t("settings.tabs.email"),
      icon: EmailIcon,
      visible: false,
    },
    {
      type: SettingsTabs.EDelivery,
      header: t("settings.edeliveryTitle"),
      icon: OutgoingIcon,
      visible: true,
    },

    {
      type: SettingsTabs.HardDrive,
      header: t("settings.tabs.dataInNota"),
      icon: DriveIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Security,
      header: t("settings.tabs.security"),
      icon: SecurityIcon,
      visible: true,
    },
    {
      type: SettingsTabs.ViewSettings,
      header: t("settings.view.title"),
      icon: ViewIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Payments,
      header: t("settings.tabs.payments"),
      icon: DiskIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Dictionaries,
      header: "Słowniki",
      icon: BookIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Workflow,
      header: "Workflow",
      icon: ShareIcon,
      visible: true,
    },
    {
      type: SettingsTabs.BudzetFk,
      header: "Budżet i FK",
      icon: HomeWorkIcon,
      visible: true,
    },
    {
      type: SettingsTabs.Mailroom,
      header: "Biuro podawcze",
      icon: FlagIcon,
      visible: true,
    },
  ];

  const contentKey = `${tab}`;

  const handleCloseModal = () => {
    setModals({ ...modals, settings: false });
    onClose?.();
  };

  const handleTabClick = (tab: SettingsTabs) => {
    setTab(tab);
  };

  return (
    <div className={styles.modal}>
      <div className={styles.window}>
        <div className={styles.wrapper}>
          <div className={styles.titleWrapper}>
            <div className={styles.title}>
              <div className={styles.t}>
                {label ? label : t("settings.title")}

                {/* {!isGuest && (
                  <div className={styles.subTitle}>
                    {<ActiveTabIcon className={styles.icon} />}
                    {tabs[tab].header}
                  </div>
                )} */}
              </div>
              <CloseIcon
                onClick={handleCloseModal}
                className={styles.closeIcon}
              />
            </div>
          </div>
          <div className={styles.layout}>
            {!isGuest && (
              <div className={styles.tabs}>
                <div className={styles.tabsGroup}>
                  <div className={styles.groupTitle}>Ustawienia firmy</div>
                  {tabs
                    .filter((x) => x.visible)
                    .map((item, i) => (
                      <TabButton
                        key={`settings_tab_${i}`}
                        title={item.header}
                        Icon={item.icon}
                        isActive={tab == item.type}
                        handleClick={() => handleTabClick(item.type)}
                      ></TabButton>
                    ))}
                </div>
              </div>
            )}

            {isGuest && (
              <div className={styles.tabs}>
                {guestTabs.map((t) => (
                  <TabButton
                    title={t.header}
                    Icon={t.icon}
                    isActive={tab == t.type}
                    handleClick={() => handleTabClick(t.type)}
                  ></TabButton>
                ))}
              </div>
            )}

            <div className={styles.content}>
              <div key={contentKey} className={styles.tabContent}>
                {tab === SettingsTabs.Basic && <BasicTab></BasicTab>}
                {tab === SettingsTabs.PersonalData && (
                  <PersonalTab
                    showTabBar={(v) => setShowTabBar(v)}
                  ></PersonalTab>
                )}
                {tab === SettingsTabs.Workers && <WorkersTab />}
                {tab === SettingsTabs.HardDrive && <DiskTab></DiskTab>}
                {tab === SettingsTabs.Integrations && (
                  <IntegrationsTab
                    showTabBar={(v) => setShowTabBar(v)}
                  ></IntegrationsTab>
                )}

                {tab === SettingsTabs.Security && (
                  <SecurityTab
                    showTabBar={(v) => setShowTabBar(v)}
                  ></SecurityTab>
                )}
                {tab === SettingsTabs.EDelivery && (
                  <EDeliveryTab
                    showTabBar={(v) => setShowTabBar(v)}
                  ></EDeliveryTab>
                )}
                {tab === SettingsTabs.Classification && (
                  <ClassificationTab
                  // showTabBar={(v) => setShowTabBar(v)}
                  ></ClassificationTab>
                )}
                {tab === SettingsTabs.ClassificationStatus && (
                  <ClassificationStatusTab
                  // showTabBar={(v) => setShowTabBar(v)}
                  ></ClassificationStatusTab>
                )}
                {tab === SettingsTabs.Roles && (
                  <RolesTab
                  // showTabBar={(v) => setShowTabBar(v)}
                  ></RolesTab>
                )}
                {tab === SettingsTabs.Email && (
                  <EmailTab
                  // showTabBar={(v) => setShowTabBar(v)}
                  ></EmailTab>
                )}
                {tab === SettingsTabs.Payments && <PaymentsTab />}
                {tab === SettingsTabs.ViewSettings && <ViewTab />}
                {tab === SettingsTabs.Dictionaries && <DictionariesTab />}
                {tab === SettingsTabs.Workflow && <WorkflowModule />}
                {tab === SettingsTabs.Integrations1 && <Integrations1Tab />}
                {tab === SettingsTabs.ESzyfrowane && <ESzyfrowaneTab />}
                {tab === SettingsTabs.BudzetFk && <BudzetFkTab />}
                {tab === SettingsTabs.Mailroom && <MailroomTab />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
